import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faPhone, faLocationDot, faComment, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

import { validateEmail } from '../utils/validate';
import { handleTokenAndPostRequest, trackEvent } from '../utils/utils';
import { computeBranding } from '../utils/useAgentDetails';

function EnquiryModal({ show, onHide, agentDetails }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [name, setName] = useState('');
  const [enquiry, setEnquiry] = useState('');
  const [message, setMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [leasingChecked, setLeasingChecked] = useState(false);
  const [sellingChecked, setSellingChecked] = useState(false);

  const hasSavedSettings = useRef(false);

  useEffect(() => {
    if (show && agentDetails && !hasSavedSettings.current) {
      hasSavedSettings.current = true;
      const saveSettings = async () => {
        try {
          await handleTokenAndPostRequest(
            `${window.api_host}/save-settings`,
            {
              agent_slug: agentDetails.landing_page_slug,
              event_type: 'start_enquiry_form',
            },
            {
              'Content-Type': 'application/json',
            }
          );
        } catch (error) {
          console.error('Error saving settings', error);
        }
      };
      saveSettings();
    }
  }, [show, agentDetails]);

  const resetForm = () => {
    setEmail('');
    setPhone('');
    setAddress('');
    setName('');
    setEnquiry('');
    setMessage('');
    setIsUpdating(false);
    setLeasingChecked(false);
    setSellingChecked(false);
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (!validateEmail(e.target.value)) {
      // set error message
    } else {
      // clear error message
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email || !phone) {
      setMessage('Please submit both your email and phone number so we can get in touch.');
      return;
    }
    trackEvent('submit_enquiry_form');
    setIsUpdating(true);

    try {
      const response = await handleTokenAndPostRequest(
        `${window.api_host}/enquiry`,
        {
          email,
          message: enquiry,
          phone,
          address,
          name,
          agency_id: agentDetails.agency_id,
          agent_id: agentDetails.agent_id,
          leasing: leasingChecked,
          selling: sellingChecked,
          agent_slug: agentDetails.landing_page_slug,
        },
        {
          'Content-Type': 'application/json',
        }
      );

      console.log(response.data);
      setIsUpdating(false);
      resetForm();
      onHide();
    } catch (error) {
      console.error('Error:', error);
      setIsUpdating(false);
      setMessage('There was an error submitting your enquiry. Please try again.');
    }
  };

  if (!agentDetails) return null;

  const { accentColor, buttonTextColor, isLight } = computeBranding(agentDetails) || {};

  const inputStyle = {
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    padding: '11px 14px',
    fontSize: '0.88rem',
    marginBottom: 8,
  };

  return (
    <Modal show={show} onHide={handleClose} fullscreen="xl-down" centered style={{ zIndex: 9999 }}>
      <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
        <Modal.Title style={{ fontSize: '1.2rem', fontWeight: 700, color: '#111', letterSpacing: '-0.01em' }}>Contact {agentDetails.agent_name?.split(' ')[0] || 'us'}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '16px 24px' }}>
        <p style={{ fontSize: '0.88rem', color: '#6b7280', lineHeight: 1.6, marginBottom: 16 }}>No visit required — we can provide an initial consultation remotely based on recent market data.</p>
        <Form className="w-100" onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, textAlign: 'left' }}>
            <Form.Check
              type="checkbox"
              label="Leasing my property"
              checked={leasingChecked}
              onChange={() => setLeasingChecked(!leasingChecked)}
              style={{ fontSize: '0.85rem' }}
            />
            <Form.Check
              type="checkbox"
              label="Selling my property"
              checked={sellingChecked}
              onChange={() => setSellingChecked(!sellingChecked)}
              style={{ fontSize: '0.85rem' }}
            />
          </div>
          <Form.Control placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          <Form.Control type="email" placeholder="Email" value={email} onChange={handleEmailChange} style={inputStyle} />
          <Form.Control type="tel" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} />
          <Form.Control placeholder="Address" value={address} onChange={(e) => setAddress(e.target.value)} style={inputStyle} />
          <Form.Control
            as="textarea"
            placeholder="Optional message"
            style={{ ...inputStyle, height: 100, resize: 'none' }}
            value={enquiry}
            onChange={(e) => setEnquiry(e.target.value)}
          />
          <Button
            type="submit"
            style={{
              width: '100%',
              backgroundColor: accentColor,
              borderColor: isLight ? '#222' : accentColor,
              color: buttonTextColor,
              borderRadius: 12,
              padding: '13px 0',
              fontWeight: 600,
              fontSize: '0.95rem',
              boxShadow: `0 4px 16px ${accentColor}30`,
              marginTop: 4,
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {isUpdating ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 8 }} />Send Message</>}
          </Button>
          {message && (
            <Alert variant="secondary" className="mt-3" style={{ borderRadius: 12, border: 'none', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '0.88rem', textAlign: 'center' }}>
              {message}
            </Alert>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default EnquiryModal;
